import type { ConnectionHealthItem } from '@borradh-workspace/contracts';
import { Link } from '@tanstack/react-router';
import {
  CreditCard,
  ExternalLink,
  type LucideIcon,
  Unplug,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ROUTES } from '@/lib/route-paths';

/**
 * Broken connections as items inside "Today's Update" on the dashboard home.
 *
 * ONE CARD PER PROBLEM, stacked under the "Today's Update" heading alongside
 * the appointments-and-messages card. Each connection needs a DIFFERENT thing
 * from the reader: Instagram wants a 30-second reconnect, an abandoned Stripe
 * onboarding wants finishing, and an account Meta has locked cannot be fixed
 * here at all. Sharing one card would mean sharing one button and burying the
 * rest.
 *
 * WHY THEY ARE ON HOME. The production audit of 2026-08-24 found 33 dead
 * connections across 21 organisations, several down for over two months, taking
 * hundreds of customer messages a week that nobody could answer. The only
 * surface that said so was a banner on the Integrations page — somewhere you
 * have no reason to open unless you already suspect a problem.
 *
 * They come first: an appointment list is not much use if the clinic cannot
 * reply to the person asking to book.
 */
export function ConnectionHealthUpdate({
  problems,
}: {
  problems: ConnectionHealthItem[];
}) {
  // One item per PROVIDER. Several dead Pages on one Meta account are one
  // problem to the reader and one reconnect, not five identical items.
  const byProvider = problems.filter(
    (problem, index) =>
      problems.findIndex(
        (p) =>
          p.provider === problem.provider &&
          (p.provider !== 'meta_whatsapp' ||
            (p.reasonCode === problem.reasonCode &&
              p.remedy === problem.remedy))
      ) === index
  );

  return (
    <>
      {byProvider.map((problem) => (
        <ConnectionProblemCard
          key={`${problem.provider}:${problem.reasonCode}:${problem.remedy}`}
          problem={
            problem.provider === 'meta_whatsapp'
              ? {
                  ...problem,
                  label:
                    problems
                      .filter(
                        (p) =>
                          p.provider === problem.provider &&
                          p.reasonCode === problem.reasonCode &&
                          p.remedy === problem.remedy
                      )
                      .map((p) => p.label)
                      .filter(Boolean)
                      .join(', ') || null,
                }
              : problem
          }
        />
      ))}
    </>
  );
}

function ConnectionProblemCard({ problem }: { problem: ConnectionHealthItem }) {
  const Icon: LucideIcon =
    problem.provider === 'stripe_connect' ||
    problem.reasonCode === 'meta.whatsapp_payment_blocked'
      ? CreditCard
      : Unplug;

  return (
    <div
      role="alert"
      className="rounded-2xl border border-border bg-card p-5 shadow-xs"
    >
      <div className="flex items-start gap-3">
        <Icon
          className="mt-0.5 size-5 shrink-0 text-destructive"
          strokeWidth={2}
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-destructive">
            {headline(problem)}
          </p>
          <p className="mt-1 text-sm italic text-muted-foreground">
            {problem.provider === 'meta_whatsapp' && problem.label
              ? `${problem.label}: `
              : ''}
            {problem.detail ?? 'This connection needs attention.'}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <ProblemAction problem={problem} />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Leads with the consequence, not the provider name. "Your Facebook Page has
 * stopped replying" is something a clinic owner recognises; "meta_page:
 * action_required" is not.
 */
function headline(problem: ConnectionHealthItem): string {
  const days = daysSince(problem.stateSince);
  const since =
    days >= 1
      ? ` — ${days === 1 ? 'since yesterday' : `for ${days} days`}`
      : '';

  if (problem.reasonCode === 'meta.whatsapp_payment_blocked') {
    return `WhatsApp needs a payment method update${since}`;
  }
  if (problem.provider === 'stripe_connect') {
    return `You can't take payments yet${since}`;
  }
  if (problem.remedy === 'finish_setup') {
    return `${problem.providerLabel} isn't finished setting up${since}`;
  }
  return `${problem.providerLabel} has stopped working${since}`;
}

/**
 * The button, chosen by remedy.
 *
 * `contact_provider` must never render a reconnect link: Meta has restricted
 * the person's own account and our OAuth flow cannot lift that. Where the error
 * registry gave us the provider's own URL, they go there instead.
 */
function ProblemAction({ problem }: { problem: ConnectionHealthItem }) {
  if (problem.provider === 'meta_whatsapp') {
    return (
      <Button asChild variant="outline" size="sm">
        <Link to={ROUTES.integrations}>Integrations</Link>
      </Button>
    );
  }

  if (problem.remedy === 'contact_provider' && problem.actionUrl) {
    return (
      <Button asChild variant="outline" size="sm">
        <a href={problem.actionUrl} target="_blank" rel="noreferrer">
          {problem.actionLabel ?? `Resolve with ${problem.providerLabel}`}
          <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
        </a>
      </Button>
    );
  }

  const to =
    problem.provider === 'stripe_connect'
      ? ROUTES.settingsPayments
      : ROUTES.integrations;

  return (
    <Button asChild variant="outline" size="sm">
      <Link to={to}>
        {problem.remedy === 'finish_setup'
          ? 'Finish setup'
          : `Reconnect ${problem.providerLabel}`}
      </Link>
    </Button>
  );
}

function daysSince(iso: string): number {
  const ms = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.floor(ms / (24 * 60 * 60 * 1000)));
}
